import React, { useState } from 'react';
import PropTypes from 'prop-types';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import KruozAPI from '../../../KruozAPI';

const PreviewSubmitStep = ({ formData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const navigate = useNavigate();

  const renderRoomType = () => {
    const roomTypes = {
      small_portable: { label: 'Small Portable', image: '/room-types/small-portable.jpg' },
      medium_portable: { label: 'Medium Portable', image: '/room-types/medium-portable.jpg' },
      large_portable: { label: 'Large Portable', image: '/room-types/large-portable.jpg' },
      small_walkin: { label: 'Small Walk-in', image: '/room-types/small-walkin.jpg' },
      medium_walkin: { label: 'Medium Walk-in', image: '/room-types/medium-walkin.jpg' },
      large_walkin: { label: 'Large Walk-in', image: '/room-types/large-walkin.jpg' },
      custom: { label: 'Custom Size', image: '/room-types/custom.jpg' },
    };
    const selectedRoom = roomTypes[formData.roomType];
    return selectedRoom ? (
      <div className="flex items-center bg-gray-100 p-2 rounded">
        <img src={selectedRoom.image} alt={selectedRoom.label} className="w-20 h-20 object-cover rounded mr-4" />
        <span className="text-lg font-semibold">{selectedRoom.label}</span>
      </div>
    ) : formData.roomType;
  };

  const SectionCard = ({ title, children }) => (
    <div className="bg-white shadow-md rounded-lg p-6 mb-6">
      <h3 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-200">{title}</h3>
      {children}
    </div>
  );

  const InfoRow = ({ label, value }) => {
    const isNA =
      value === null ||
      value === undefined ||
      (typeof value === 'string' &&
        (value.trim() === '' ||
         value.trim().toLowerCase() === 'n/a' ||
         value.toLowerCase().includes('undefined') ||
         value.toLowerCase().includes('null')));

    if (isNA) return null;

    return (
      <div className="flex flex-wrap mb-2">
        <span className="w-full sm:w-1/3 font-medium text-gray-600">{label}:</span>
        <span className="w-full sm:w-2/3">{value}</span>
      </div>
    );
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const toastId = toast.loading("Submitting your coolroom request...");
    
    try {
      const response = await KruozAPI.post('/Quotation/CoolroomRequest', formData);
      
      if (response.status === 200) {
        toast.success("Coolroom request submitted successfully!", { id: toastId });
        navigate('/customer-home');
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting coolroom request:', error);
      toast.error("Failed to submit coolroom request. Please try again.", { id: toastId });
    } finally {
      setIsSubmitting(false);
      setShowConfirmDialog(false);
    }
  };

  const ConfirmationDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Confirm Submission</h2>
        <p className="mb-6">
          Are you sure you want to submit this coolroom request? You will not be able to change the request data after this submission.
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => setShowConfirmDialog(false)}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />
      
      <SectionCard title="Location">
        <InfoRow label="Delivery Address" value={formData.deliveryAddress} />
        <InfoRow label="Special Conditions" value={formData.specialConditions} />
        <InfoRow label="Site Type" value={formData.siteType} />
        <InfoRow label="Power Supply" value={formData.powerSupply} />
      </SectionCard>

      <SectionCard title="Room Size & Capacity">
        <InfoRow label="Room Type" value={renderRoomType()} />
        <InfoRow label="Capacity" value={`${formData.capacity} cubic meters`} />
        <InfoRow label="Additional Size Comments" value={formData.sizeComment} />
      </SectionCard>

      <SectionCard title="Intended Use">
        <InfoRow label="Temperature Range" value={`${formData.minTemperature}°C to ${formData.maxTemperature}°C`} />
        <InfoRow label="Power Source" value={formData.powerSource} />
        <InfoRow label="Additional Features" value={
          [
            formData.shelving && 'Shelving',
            formData.lighting && 'Internal Lighting',
            formData.security && 'Security Features'
          ].filter(Boolean).join(', ') || 'None'
        } />
        <InfoRow label="Guest Count" value={formData.guestCount} />
        <InfoRow label="Additional Use Comments" value={formData.useComment} />
      </SectionCard>

      <SectionCard title="Hire Duration">
        <InfoRow label="Delivery Date" value={formData.deliveryDate} />
        <InfoRow label="Rental Duration" value={`${formData.rentalDuration} days`} />
        <InfoRow label="Collection Date" value={formData.collectionDate} />
        <InfoRow label="Delivery Instructions" value={formData.deliveryInstructions} />
        <InfoRow label="Additional Duration Comments" value={formData.durationComment} />
      </SectionCard>

      <SectionCard title="Contact Information">
        <InfoRow label="Full Name" value={formData.fullName} />
        <InfoRow label="Email" value={formData.email} />
        <InfoRow label="Mobile Number" value={formData.mobileNumber} />
        <InfoRow label="Company Name" value={formData.companyName} />
        <InfoRow label="Position" value={formData.position} />
        {formData.alternativeContactName && (
          <>
            <div className="mt-4 mb-2 font-semibold text-gray-700">Alternative Contact</div>
            <InfoRow label="Name" value={formData.alternativeContactName} />
            <InfoRow label="Mobile" value={formData.alternativeContactMobile} />
            <InfoRow label="Email" value={formData.alternativeContactEmail} />
          </>
        )}
      </SectionCard>

      {/* Submit button */}
      <div className="flex justify-center mt-8">
        <button
          onClick={() => setShowConfirmDialog(true)}
          disabled={isSubmitting}
          className={`bg-blue-500 text-white px-6 py-3 rounded-lg text-lg font-semibold
                      hover:bg-blue-600 transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Coolroom Request'}
        </button>
      </div>

      {showConfirmDialog && <ConfirmationDialog />}
    </div>
  );
};

PreviewSubmitStep.propTypes = {
  formData: PropTypes.object.isRequired,
};

export default PreviewSubmitStep;